import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BackArrow, InfoBlock, TopProgress } from '../../components'
import { dedicatedServersService } from '../../api'
import { useUi } from '../../context/ui.context'
import { usePageTitle } from '../../context/navigationContext'
import { extractErrorMessage } from '../../utils/error-extractor'
import { formatDate } from '../../utils/date-formatter'
import { DedicatedUser } from './types'
import { DetailContainer, DetailGrid, DetailHeader, DetailTitle, Status, StatusDot } from './styles'

const getObjectDisplay = (value: Record<string, unknown> | null) => {
  if (!value) return '-'
  const display = value.display || value.full_name || value.name || value.company || value.email || value.id
  return display !== undefined && display !== null ? String(display) : '-'
}

const getObjectInfo = (value: Record<string, unknown> | null) => {
  if (!value) return []
  return Object.entries(value).map(([key, item]) => ({
    title: key.replace(/_/g, ' '),
    value: item && typeof item === 'object' ? JSON.stringify(item) : item ?? '-',
    isLong: true,
  }))
}

const DedicatedUserDetails: FC = () => {
  const [t] = useTranslation()
  const { userId } = useParams()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [user, setUser] = useState<DedicatedUser | null>(null)
  const [loading, setLoading] = useState(true)
  const title = user ? `${t('DEDICATED_USER')} #${user.external_id}` : String(t('DEDICATED_USER'))
  usePageTitle(title)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    dedicatedServersService.getUser(userId)
      .then((response) => setUser(response.data))
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('FAILED_TO_LOAD_DEDICATED_USER'),
        })
        navigate('/dedicated-servers/users')
      })
      .finally(() => setLoading(false))
  }, [navigate, t, userId, viewAlert])

  const reasonLabel = useMemo(() => {
    if (user?.disabled_reason === 'need_payment') return t('DEDICATED_NEED_PAYMENT')
    if (user?.disabled_reason === 'admin_locked') return t('DEDICATED_ADMIN_LOCKED')
    return '-'
  }, [t, user?.disabled_reason])

  const info = useMemo(() => {
    if (!user) return []
    const clientId = user.client?.id
    return [
      { title: t('ID'), value: user.id, copy: true },
      { title: t('EXTERNAL_ID'), value: user.external_id },
      {
        title: t('CLIENT'),
        value: getObjectDisplay(user.client),
        href: clientId !== undefined && clientId !== null ? `/clients-users/clients/${String(clientId)}` : undefined,
      },
      { title: t('DEDICATED_DELETED'), value: user.deleted ? t('YES') : t('NO') },
      { title: t('DISABLED'), value: user.disabled ? t('YES') : t('NO') },
      { title: t('DEDICATED_DISABLED_REASON'), value: reasonLabel },
      { title: t('CREATED_AT'), value: formatDate(user.created_at) },
      { title: t('UPDATED_AT'), value: formatDate(user.updated_at) },
    ]
  }, [reasonLabel, t, user])

  const serviceInfo = useMemo(() => user ? [
    {
      title: t('ID'),
      value: user.service?.id ?? '-',
      href: user.service?.id ? `/billing/services/${user.service.id}` : undefined,
    },
    { title: t('NAME'), value: user.service?.display_name || '-' },
    { title: t('DOMAIN_NAME'), value: user.service?.domain_name || '-' },
    { title: t('SERVICE_STATUS'), value: user.service?.status || '-' },
  ] : [], [t, user])

  const clientInfo = useMemo(() => getObjectInfo(user?.client || null), [user?.client])

  if (loading) return <TopProgress loading />
  if (!user) return null

  const status = user.deleted ? 'deleted' : user.disabled ? 'disabled' : 'active'
  const statusText = user.deleted ? t('DEDICATED_DELETED') : user.disabled ? t('DISABLED') : t('ENABLED')

  return (
    <DetailContainer>
      <BackArrow fallbackPath="/dedicated-servers/users" />
      <DetailHeader>
        <DetailTitle>{title}</DetailTitle>
        <Status status={status}>
          <StatusDot status={status} />
          {statusText}
        </Status>
      </DetailHeader>
      <DetailGrid>
        <InfoBlock data={info} title={t('GENERAL_INFORMATION')} />
        <InfoBlock data={serviceInfo} title={t('SERVICE')} />
        {clientInfo.length > 0 && (
          <InfoBlock data={clientInfo} title={t('DEDICATED_CLIENT_DATA')} />
        )}
      </DetailGrid>
    </DetailContainer>
  )
}

export default DedicatedUserDetails
